import type { BookingService as B2BookingService } from 'owa-graph-schema';
import type BookingServiceCategoryTypeContract from 'owa-service/lib/contract/BookingServiceCategoryType';
import type BookingServiceFormInfoType from 'owa-service/lib/contract/BookingServiceFormInfoType';
import type CustomerInformationRequirementsType from 'owa-service/lib/contract/CustomerInformationRequirementsType';
import type BookingServiceCustomQuestionType from 'owa-service/lib/contract/BookingServiceCustomQuestionType';
import type BookingServicePricingType from 'owa-service/lib/contract/BookingServicePricingType';
import type InboxReminderType from 'owa-service/lib/contract/InboxReminderType';
import type BookingServiceTypeEnum from 'owa-service/lib/contract/BookingServiceTypeEnum';
import type BookingServiceStaffGroupType from 'owa-service/lib/contract/BookingServiceStaffGroupType';
import type ItemId from 'owa-service/lib/contract/ItemId';
import type SchedulingPolicy from 'owa-service/lib/contract/SchedulingPolicy';
import type EnhancedLocation from 'owa-service/lib/contract/EnhancedLocation';

import mapEnhancedLocationToPostalAddress from '../mapEnhancedLocationToPostalAddress';
import mapSchedulingPolicy from '../mapSchedulingPolicy';
import mapBookingServiceCustomQuestionType from './mapBookingServiceCustomQuestionType';
import mapBookingServiceStaffGroupType from './mapBookingServiceStaffGroupType';
import mapBookingServiceTypeEnum from './mapBookingServiceTypeEnum';
import mapBookingServicePricingType from './mapBookingServicePricingType';
import mapStaffPersonIds from './mapStaffPersonIds';
import mapInboxReminderType from './mapInboxReminderType';
import mapBookingServiceCategory from './mapBookingServiceCategory';
import mapBookingServiceFormInfoType from './mapBookingServiceFormInfoType';
import mapCustomerInformationRequirements from './mapCustomerInformationRequirements';
import convertMinutesToIsoDuration from '../../utils/convertMinutesToIsoDuration';
import type BookingService from '../../schema/BookingService';

const mapModelABookingService = (bookingService: BookingService) => {
    const {
        AdditionalInformation,
        BookingMultiStaffSupportEnabled,
        BookingServiceCategoryType,
        BookingServiceFormInfoList,
        Language,
        CategoryNames,
        CustomerInformationRequirements,
        Currency,
        CustomQuestionIds,
        PricingType,
        DefaultEmailReminderText,
        Description,
        InboxReminders,
        ExcludeFromSelfService,
        IsDefaultLocationOnline,
        IsWebrtcOnlineMeetingEnabled,
        InternalNotes,
        ServiceId,
        BookingServiceType,
        EnableSMSNotifications,
        DisplayName,
        WebUrl,
        StaffGroups,
        StaffPersonIds,
        SchedulingPolicy,
        DefaultLocation,
        DefaultDurationMinutes,
        PreBuffer,
        PostBuffer,
        Price,
        BookingCapacityInfo,
        AddSelfServiceIdForManualBookings,
        isPrivate,
    } = bookingService as BookingService;

    const mappedBookingService: B2BookingService = {
        additionalInfo: AdditionalInformation as string,
        bookingMultiStaffSupportEnabled: BookingMultiStaffSupportEnabled as boolean,
        bookingServiceCategory: mapBookingServiceCategory(
            BookingServiceCategoryType as BookingServiceCategoryTypeContract
        ),
        bookingServiceFormInfos: mapBookingServiceFormInfoType(
            BookingServiceFormInfoList as BookingServiceFormInfoType[]
        ),
        bookingServiceLanguage: Language as string,
        bookingsCustomerInformationRequirements: mapCustomerInformationRequirements(
            CustomerInformationRequirements as CustomerInformationRequirementsType
        ),
        bookingsSchedulingPolicy: mapSchedulingPolicy(SchedulingPolicy as SchedulingPolicy),
        categoryNames: CategoryNames as string[],
        currencyIsoSymbol: Currency?.CurrencyIsoSymbol as string,
        currencySymbol: Currency?.CurrencySymbol as string,
        customQuestions: mapBookingServiceCustomQuestionType(
            CustomQuestionIds as BookingServiceCustomQuestionType[]
        ),
        defaultDuration: convertMinutesToIsoDuration(DefaultDurationMinutes as number),
        defaultLocation: {
            address: mapEnhancedLocationToPostalAddress(DefaultLocation as EnhancedLocation),
            coordinates: {
                latitude: DefaultLocation?.PostalAddress?.Latitude,
                longitude: DefaultLocation?.PostalAddress?.Longitude,
            },
            displayName: DefaultLocation?.DisplayName as string,
        },
        defaultPrice: Price as number,
        defaultPriceType: mapBookingServicePricingType(PricingType as BookingServicePricingType),
        defaultReminders: {
            message: DefaultEmailReminderText as string,
        },
        description: Description as string,
        emailReminders: mapInboxReminderType(InboxReminders as InboxReminderType[] | undefined),
        isHiddenFromCustomers: ExcludeFromSelfService as boolean,
        isLocationOnline: IsDefaultLocationOnline as boolean,
        isWebrtcOnlineMeetingEnabled: IsWebrtcOnlineMeetingEnabled as boolean,
        notes: InternalNotes as string,
        postBuffer: convertMinutesToIsoDuration(PostBuffer as number),
        preBuffer: convertMinutesToIsoDuration(PreBuffer as number),
        serviceId: ServiceId as string,
        serviceType: mapBookingServiceTypeEnum(BookingServiceType as BookingServiceTypeEnum),
        smsNotificationsEnabled: EnableSMSNotifications as boolean,
        staffGroups: mapBookingServiceStaffGroupType(StaffGroups as BookingServiceStaffGroupType[]),
        staffMemberIds: mapStaffPersonIds(StaffPersonIds as ItemId[]),
        maximumAttendeesCount: BookingCapacityInfo?.TotalCapacity as number,
        title: DisplayName as string,
        webUrl: WebUrl as string,
        canCustomerManageTheirBooking: AddSelfServiceIdForManualBookings as boolean,
        isPrivate: isPrivate as boolean,
    };
    return mappedBookingService as B2BookingService;
};

export default mapModelABookingService;
